import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChefHat, ShoppingBag, Calendar, ArrowRight, Plus, Minus, Users, Info } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { localizedField as i18nLocalized } from '../utils/i18nFields';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { tr, enUS, me } from 'date-fns/locale';
import ProductDetailModal from '../components/ProductDetailModal';
import { calculateCateringTotal, getCateringBreakdown, BASE_PRICE } from '../utils/pricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Catering = () => {
  const [cateringMenu, setCateringMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [itemQuantities, setItemQuantities] = useState({});
  const [selectedCateringItem, setSelectedCateringItem] = useState(null);
  const { addItem } = useCart();
  const { language } = useLanguage();

  // Çeviri metinleri
  const texts = {
    tr: {
      pageTitle: 'Günlük Menü',
      pageSubtitle: 'Her gün değişen özel menümüz',
      noMenu: 'Bugün için catering menüsü bulunmamıyor.',
      checkBack: 'Lütfen daha sonra tekrar kontrol edin.',
      goMenu: 'Alakart Menüye Git',
      todaysMenu: 'Bugünkü Menü',
      pricingInfo: 'Her 4 Kap = €10',
      pricingDesc: 'Her 4 kategoriden 1er tane = €10. 2ser = €20. Ayni kategoriden ekstra = urun fiyati. 4ten az = bireysel fiyat.',
      soups: 'Çorbalar',
      mainCourses: 'Ana Yemekler',
      sideDishes: 'Yardımcı Yemekler',
      salads: 'Salatalar',
      selected: 'seçili',
      outOfStock: 'Tükendi',
      inStock: 'adet',
      orderSummary: 'Sipariş Özeti',
      persons: 'Kişi',
      totalSelected: 'Toplam Seçilen',
      dishes: 'kap',
      sets: 'set',
      extra: 'ekstra',
      perPerson: 'Kişi Başı',
      total: 'Toplam',
      addToCart: 'Sepete Ekle',
      selectPerson: 'Lütfen en az 1 kişi seçin',
      selectItem: 'Lütfen en az 1 ürün seçin',
      addedSuccess: 'kişilik catering menüsü sepete eklendi',
      menuFetchError: 'Catering menüsü yüklenirken hata oluştu',
      soldOut: 'Bu ürün tükendi',
      stockLimit: 'Stokta sadece',
      items: 'adet var'
    },
    en: {
      pageTitle: 'Daily Menu',
      pageSubtitle: 'Our special menu changes every day',
      noMenu: 'No catering menu available for today.',
      checkBack: 'Please check back later.',
      goMenu: 'Go to A La Carte Menu',
      todaysMenu: "Today's Menu",
      pricingInfo: 'Every 4 Dishes = €10',
      pricingDesc: '1 from each of 4 categories = €10. 2 each = €20. Same category extras = item price. Under 4 = individual prices.',
      soups: 'Soups',
      mainCourses: 'Main Courses',
      sideDishes: 'Side Dishes',
      salads: 'Salads',
      selected: 'selected',
      outOfStock: 'Out of Stock',
      inStock: 'pcs',
      orderSummary: 'Order Summary',
      persons: 'Persons',
      totalSelected: 'Total Selected',
      dishes: 'dishes',
      sets: 'set',
      extra: 'extra',
      perPerson: 'Per Person',
      total: 'Total',
      addToCart: 'Add to Cart',
      selectPerson: 'Please select at least 1 person',
      selectItem: 'Please select at least 1 item',
      addedSuccess: 'person catering menu added to cart',
      menuFetchError: 'Error loading catering menu',
      soldOut: 'This item is sold out',
      stockLimit: 'Only',
      items: 'in stock'
    },
    me: {
      pageTitle: 'Dnevni Meni',
      pageSubtitle: 'Naš specijalni meni se mijenja svaki dan',
      noMenu: 'Nema ketering menija za danas.',
      checkBack: 'Molimo provjerite kasnije.',
      goMenu: 'Idite na A La Carte Meni',
      todaysMenu: 'Današnji Meni',
      pricingInfo: 'Svaka 4 Jela = €10',
      pricingDesc: '1 iz svake od 4 kategorije = €10. Po 2 = €20. Dodatni artikli = cijena. Manje od 4 = pojedinacne cijene.',
      soups: 'Supe',
      mainCourses: 'Glavna Jela',
      sideDishes: 'Prilozi',
      salads: 'Salate',
      selected: 'odabrano',
      outOfStock: 'Nema',
      inStock: 'kom',
      orderSummary: 'Pregled Narudžbe',
      persons: 'Osobe',
      totalSelected: 'Ukupno Odabrano',
      dishes: 'jela',
      sets: 'set',
      extra: 'dodatno',
      perPerson: 'Po Osobi',
      total: 'Ukupno',
      addToCart: 'Dodaj u Korpu',
      selectPerson: 'Molimo odaberite najmanje 1 osobu',
      selectItem: 'Molimo odaberite najmanje 1 artikal',
      addedSuccess: 'osoba ketering meni dodat u korpu',
      menuFetchError: 'Greška pri učitavanju ketering menija',
      soldOut: 'Ovaj artikal je rasprodat',
      stockLimit: 'Samo',
      items: 'na stanju'
    },
    ru: {
      pageTitle: 'Дневное Меню',
      pageSubtitle: 'Наше специальное меню меняется каждый день',
      noMenu: 'Кейтеринг меню на сегодня недоступно.',
      checkBack: 'Пожалуйста, проверьте позже.',
      goMenu: 'Перейти в А Ла Карт Меню',
      todaysMenu: 'Сегодняшнее Меню',
      pricingInfo: 'Каждые 4 Блюда = €10',
      pricingDesc: '1 из каждой из 4 категорий = €10. По 2 = €20. Дополнительные = цена блюда. Меньше 4 = индивидуальные цены.',
      soups: 'Супы',
      mainCourses: 'Основные блюда',
      sideDishes: 'Гарниры',
      salads: 'Салаты',
      selected: 'выбрано',
      outOfStock: 'Нет',
      inStock: 'шт',
      orderSummary: 'Сводка Заказа',
      persons: 'Человек',
      totalSelected: 'Всего Выбрано',
      dishes: 'блюд',
      sets: 'набор',
      extra: 'дополнительно',
      perPerson: 'На Человека',
      total: 'Итого',
      addToCart: 'В корзину',
      selectPerson: 'Пожалуйста, выберите минимум 1 человека',
      selectItem: 'Пожалуйста, выберите минимум 1 блюдо',
      addedSuccess: 'персональное кейтеринг меню добавлено в корзину',
      menuFetchError: 'Ошибка загрузки кейтеринг меню',
      soldOut: 'Это блюдо распродано',
      stockLimit: 'Только',
      items: 'в наличии'
    },
    sr: {
      pageTitle: 'Дневни Мени',
      pageSubtitle: 'Наш специјални мени се мења сваки дан',
      noMenu: 'Нема кетеринг менија за данас.',
      checkBack: 'Молимо проверите касније.',
      goMenu: 'Идите на А Ла Карт Мени',
      todaysMenu: 'Данашњи Мени',
      pricingInfo: 'Свака 4 Јела = €10',
      pricingDesc: '1 из сваке од 4 категорије = €10. По 2 = €20. Додатни артикли = цена. Мање од 4 = појединачне цене.',
      soups: 'Супе',
      mainCourses: 'Главна Јела',
      sideDishes: 'Прилози',
      salads: 'Салате',
      selected: 'одабрано',
      outOfStock: 'Нема',
      inStock: 'ком',
      orderSummary: 'Преглед Поруџбине',
      persons: 'Особе',
      totalSelected: 'Укупно Одабрано',
      dishes: 'јела',
      sets: 'сет',
      extra: 'додатно',
      perPerson: 'По Особи',
      total: 'Укупно',
      addToCart: 'Додај у Корпу',
      selectPerson: 'Молимо одаберите најмање 1 особу',
      selectItem: 'Молимо одаберите најмање 1 артикал',
      addedSuccess: 'особа кетеринг мени додат у корпу',
      menuFetchError: 'Грешка при учитавању кетеринг менија',
      soldOut: 'Овај артикал је распродат',
      stockLimit: 'Само',
      items: 'на стању'
    }
  };

  const txt = texts[language] || texts.en;
  const dateLocale = language === 'tr' ? tr : language === 'me' ? tr : enUS;

  useEffect(() => {
    fetchTodayMenu();
  }, []);

  const fetchTodayMenu = async () => {
    try {
      const response = await axios.get(`${API}/catering/today`);
      setCateringMenu(response.data);
      // Başlangıçta hiçbir şey seçili değil
      setItemQuantities({});
    } catch (error) {
      console.error('Catering fetch error:', error);
      toast.error(txt.menuFetchError);
    } finally {
      setLoading(false);
    }
  };

  // Kategorideki toplam seçim sayısı
  const getCategoryTotal = (items) => {
    return items.reduce((total, item) => total + (itemQuantities[item.id] || 0), 0);
  };

  // Toplam seçilen kap sayısı
  const getTotalItemCount = () => {
    return Object.values(itemQuantities).reduce((total, qty) => total + qty, 0);
  };

  const calculateUnitPrice = () => calculateCateringTotal(cateringMenu, itemQuantities);
  const getPriceBreakdown = () => getCateringBreakdown(cateringMenu, itemQuantities);

  // Bir ürünün stokunu al
  const getItemStock = (itemId) => {
    if (!cateringMenu) return 0;
    const allCategories = [
      cateringMenu.corbalar || [],
      cateringMenu.ana_yemekler || [],
      cateringMenu.yardimci_yemekler || [],
      cateringMenu.salatalar || [],
      cateringMenu.mezeler || []
    ];
    for (const category of allCategories) {
      const item = category.find(i => i.id === itemId);
      if (item) return item.stock ?? 100;
    }
    return 0;
  };

  const calculateTotal = () => {
    return calculateUnitPrice() * quantity;
  };

  // Miktar artır
  const increaseQuantity = (itemId) => {
    const stock = getItemStock(itemId);
    const currentQty = itemQuantities[itemId] || 0;
    
    if (stock <= 0) {
      toast.error(txt.soldOut);
      return;
    }
    
    if (currentQty >= stock) {
      toast.error(`${txt.stockLimit} ${stock} ${txt.items}`);
      return;
    }
    
    setItemQuantities(prev => ({
      ...prev,
      [itemId]: currentQty + 1
    }));
  };

  // Miktar azalt
  const decreaseQuantity = (itemId) => {
    const currentQty = itemQuantities[itemId] || 0;
    if (currentQty <= 0) return;
    
    setItemQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(0, currentQty - 1)
    }));
  };

  // Seçimleri detaylı olarak al
  const getSelectionsDetail = () => {
    if (!cateringMenu) return {};
    
    const selections = {
      corbalar: [],
      ana_yemekler: [],
      yardimci_yemekler: [],
      salatalar: [],
      mezeler: []
    };
    
    (cateringMenu.corbalar || []).forEach(item => {
      const qty = itemQuantities[item.id] || 0;
      if (qty > 0) selections.corbalar.push({ ...item, quantity: qty });
    });
    
    (cateringMenu.ana_yemekler || []).forEach(item => {
      const qty = itemQuantities[item.id] || 0;
      if (qty > 0) selections.ana_yemekler.push({ ...item, quantity: qty });
    });
    
    (cateringMenu.yardimci_yemekler || []).forEach(item => {
      const qty = itemQuantities[item.id] || 0;
      if (qty > 0) selections.yardimci_yemekler.push({ ...item, quantity: qty });
    });
    
    (cateringMenu.salatalar || []).forEach(item => {
      const qty = itemQuantities[item.id] || 0;
      if (qty > 0) selections.salatalar.push({ ...item, quantity: qty });
    });

    (cateringMenu.mezeler || []).forEach(item => {
      const qty = itemQuantities[item.id] || 0;
      if (qty > 0) selections.mezeler.push({ ...item, quantity: qty });
    });
    
    return selections;
  };

  const handleAddToCart = () => {
    if (quantity < 1) {
      toast.error(txt.selectPerson);
      return;
    }

    const totalItems = getTotalItemCount();
    if (totalItems < 1) {
      toast.error(txt.selectItem);
      return;
    }

    const selections = getSelectionsDetail();
    
    // Detaylı açıklama oluştur
    const detailParts = [];
    if (selections.corbalar.length > 0) {
      detailParts.push(txt.soups + ': ' + selections.corbalar.map(s => `${s.quantity}x ${s.name}`).join(', '));
    }
    if (selections.ana_yemekler.length > 0) {
      detailParts.push(txt.mainCourses + ': ' + selections.ana_yemekler.map(s => `${s.quantity}x ${s.name}`).join(', '));
    }
    if (selections.yardimci_yemekler.length > 0) {
      detailParts.push(txt.sideDishes + ': ' + selections.yardimci_yemekler.map(s => `${s.quantity}x ${s.name}`).join(', '));
    }
    if (selections.salatalar.length > 0) {
      detailParts.push(txt.salads + ': ' + selections.salatalar.map(s => `${s.quantity}x ${s.name}`).join(', '));
    }
    if (selections.mezeler.length > 0) {
      detailParts.push('Mezeler: ' + selections.mezeler.map(s => `${s.quantity}x ${s.name}`).join(', '));
    }

    const menuName = language === 'tr' ? `Günlük Menü (${format(new Date(cateringMenu.date), 'd MMMM', { locale: dateLocale })})` :
                     language === 'me' ? `Dnevni Meni (${format(new Date(cateringMenu.date), 'd MMMM', { locale: dateLocale })})` :
                     `Daily Menu (${format(new Date(cateringMenu.date), 'd MMMM', { locale: dateLocale })})`;

    addItem({
      item_id: `catering-${cateringMenu.date}-${Date.now()}`,
      name: menuName,
      name_tr: `Günlük Menü (${format(new Date(cateringMenu.date), 'd MMMM', { locale: dateLocale })})`,
      price: calculateUnitPrice(),
      quantity: quantity,
      item_type: 'catering',
      catering_selections: selections,
      catering_detail: detailParts.join(' | ')
    });
    
    toast.success(`${quantity} ${txt.addedSuccess}`);
    setQuantity(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-terracotta border-t-transparent" />
      </div>
    );
  }

  if (!cateringMenu) {
    return (
      <div className="min-h-screen bg-bone">
        <div className="bg-sand py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-4">
              {txt.pageTitle}
            </h1>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <ChefHat className="w-16 h-16 text-charcoal/30 mx-auto mb-6" />
          <h2 className="font-playfair text-2xl font-semibold text-charcoal mb-4">
            {txt.noMenu}
          </h2>
          <p className="text-charcoal/60 mb-8">
            {txt.checkBack}
          </p>
          <Button
            onClick={() => window.location.href = '/alakart'}
            className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full px-8"
            data-testid="goto-alakart-btn"
          >
            {txt.goMenu}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  const categories = [
    { key: 'corbalar', title: txt.soups, items: cateringMenu.corbalar || [], icon: '🍲' },
    { key: 'ana_yemekler', title: txt.mainCourses, items: cateringMenu.ana_yemekler || [], icon: '🍖' },
    { key: 'yardimci_yemekler', title: txt.sideDishes, items: cateringMenu.yardimci_yemekler || [], icon: '🍚' },
    { key: 'salatalar', title: txt.salads, items: cateringMenu.salatalar || [], icon: '🥗' },
    ...((cateringMenu.mezeler || []).length > 0 ? [{ key: 'mezeler', title: 'Mezeler', items: cateringMenu.mezeler || [], icon: '🧆' }] : [])
  ];

  const totalItems = getTotalItemCount();
  const { sets, extraItems } = getPriceBreakdown();

  return (
    <div className="min-h-screen bg-bone pb-36 md:pb-52">
      {/* Header */}
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-5 h-5 text-terracotta" />
              <span className="text-terracotta font-medium">
                {format(new Date(cateringMenu.date), 'd MMMM yyyy, EEEE', { locale: dateLocale })}
              </span>
            </div>
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-4">
              {txt.pageTitle}
            </h1>
            
            {/* Fiyat Bilgisi Banner */}
            <div className="bg-basil/10 border border-basil/20 rounded-xl p-4 mt-6">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-basil mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-charcoal">{txt.pricingInfo}</p>
                  <p className="text-charcoal/70 text-sm">
                    {txt.pricingDesc}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Menu Builder */}
        <div className="space-y-8">
          {categories.map((category, catIndex) => {
            const categoryTotal = getCategoryTotal(category.items);
            
            return (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIndex * 0.1 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{category.icon}</span>
                  <h2 className="font-playfair text-2xl font-semibold text-charcoal">
                    {category.title}
                  </h2>
                  <span className={`text-sm px-3 py-1 rounded-full font-medium ${
                    categoryTotal >= 1 
                      ? 'bg-basil/10 text-basil' 
                      : 'bg-stone-100 text-charcoal/60'
                  }`}>
                    {categoryTotal} {txt.dishes} {txt.selected}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {category.items.map((item) => {
                    const itemQty = itemQuantities[item.id] || 0;
                    const isSelected = itemQty > 0;
                    const itemStock = item.stock ?? 100; // Stok değeri, varsayılan 100
                    const imageUrl = item.image_url ? (item.image_url.startsWith('/api') ? `${API.replace('/api', '')}${item.image_url}` : item.image_url) : null;
                    const itemName = i18nLocalized(item, 'name', language);
                    const itemDesc = i18nLocalized(item, 'description', language);
                    
                    return (
                      <div
                        key={item.id}
                        className={`relative rounded-2xl transition-all overflow-hidden cursor-pointer ${
                          itemStock <= 0 
                            ? 'bg-stone-100 opacity-60' 
                            : isSelected 
                              ? 'bg-basil text-white shadow-lg ring-2 ring-basil' 
                              : 'bg-white hover:shadow-md border border-stone-200'
                        }`}
                        data-testid={`catering-option-${item.id}`}
                        onClick={() => setSelectedCateringItem({
                          ...item,
                          _displayName: itemName,
                          _displayDesc: itemDesc,
                          _displayCategory: category.title,
                          price: item.price_addition || 5
                        })}
                      >
                        {imageUrl && (
                          <div className="w-full h-32 overflow-hidden relative">
                            <img src={imageUrl} alt={itemName} className="w-full h-full object-cover" />
                            {itemStock <= 0 && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <span className="text-white font-bold text-sm">{txt.outOfStock.toUpperCase()}</span>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="p-5">
                          <div className="mb-3">
                            <div className="flex items-center justify-between">
                              <h3 className={`font-semibold text-lg ${isSelected ? 'text-white' : 'text-charcoal'}`}>
                                {itemName}
                              </h3>
                              {/* Stok Göstergesi */}
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                itemStock <= 0 
                                  ? 'bg-red-100 text-red-700' 
                                  : itemStock <= 10 
                                    ? 'bg-amber-100 text-amber-700' 
                                    : isSelected 
                                      ? 'bg-white/20 text-white' 
                                      : 'bg-green-100 text-green-700'
                              }`}>
                                                              {itemStock <= 0 ? txt.outOfStock : `${itemStock} ${txt.inStock}`}
                              </span>
                            </div>
                            <p className={`text-sm ${isSelected ? 'text-white/80' : 'text-charcoal/60'}`}>
                              {itemDesc}
                            </p>
                            <p className={`text-xs mt-1 ${isSelected ? 'text-white/70' : 'text-charcoal/50'}`}>
                              {item.price_addition > 0 ? `Ekstra: +€${item.price_addition}` : 'Set dahil'}
                            </p>
                          </div>
                        
                          {/* Quantity Controls */}
                          <div className="flex items-center justify-center gap-3 mt-4" onClick={e => e.stopPropagation()}>
                            <button
                              onClick={() => decreaseQuantity(item.id)}
                              disabled={itemStock <= 0}
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                itemStock <= 0 
                                  ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                                  : isSelected 
                                    ? 'bg-white/20 hover:bg-white/30 text-white' 
                                    : 'bg-stone-100 hover:bg-stone-200 text-charcoal'
                              }`}
                              data-testid={`decrease-${item.id}`}
                            >
                              <Minus className="w-5 h-5" />
                            </button>
                            
                            <span className={`text-2xl font-bold min-w-[40px] text-center ${
                              isSelected ? 'text-white' : 'text-charcoal'
                            }`}>
                              {itemQty}
                            </span>
                            
                            <button
                              onClick={() => increaseQuantity(item.id)}
                              disabled={itemStock <= 0 || itemQty >= itemStock}
                              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                itemStock <= 0 || itemQty >= itemStock
                                  ? 'bg-stone-200 text-stone-400 cursor-not-allowed'
                                  : isSelected 
                                    ? 'bg-white/20 hover:bg-white/30 text-white' 
                                    : 'bg-terracotta hover:bg-terracotta/90 text-white'
                              }`}
                              data-testid={`increase-${item.id}`}
                            >
                              <Plus className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Fixed Bottom Bar - Mobile Safe - above Emergent badge */}
      <div className="fixed bottom-12 left-0 right-0 bg-white border-t border-stone-200 z-[9999] shadow-lg rounded-t-xl">
        <div className="max-w-7xl mx-auto px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          {/* Seçim Özeti - Mobilde daha kompakt */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-2 text-xs">
            <div className="flex items-center gap-1 px-2 py-1 bg-basil/10 rounded-full">
              <span className="text-charcoal/60">{txt.totalSelected}:</span>
              <span className="font-bold text-basil">{totalItems} {txt.dishes}</span>
            </div>
            {extraItems === 0 && totalItems > 0 && sets > 0 ? (
              <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-full">
                <span className="text-green-700 font-medium">{sets} set = {'\u20AC'}{sets * 10}</span>
              </div>
            ) : extraItems > 0 ? (
              <div className="flex items-center gap-1 px-2 py-1 bg-terracotta/10 rounded-full">
                <span className="text-terracotta font-medium">{sets} set + {extraItems} ekstra</span>
              </div>
            ) : null}
          </div>

          {/* Mobil Layout */}
          <div className="flex items-center justify-between gap-2">
            {/* Kişi Sayısı - Kompakt */}
            <div className="flex items-center gap-1 bg-stone-100 rounded-full p-0.5">
              <button
                onClick={() => setQuantity(Math.max(0.5, quantity - 0.5))}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm active:bg-stone-50"
                data-testid="catering-quantity-minus"
              >
                <Minus className="w-4 h-4 text-charcoal" />
              </button>
              <span className="w-8 text-center font-bold text-sm">{quantity % 1 === 0 ? quantity : quantity.toFixed(1)}</span>
              <button
                onClick={() => setQuantity(quantity + 0.5)}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm active:bg-stone-50"
                data-testid="catering-quantity-plus"
              >
                <Plus className="w-4 h-4 text-charcoal" />
              </button>
            </div>

            {/* Fiyat */}
            <div className="text-center flex-1">
              <p className="font-playfair text-lg font-bold text-charcoal">
                €{calculateTotal().toFixed(2)}
              </p>
            </div>

            {/* Sepete Ekle Butonu */}
            <Button
              onClick={handleAddToCart}
              disabled={totalItems < 1}
              className="bg-terracotta text-white hover:bg-terracotta/90 active:bg-terracotta/80 rounded-full px-4 py-2.5 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-catering-to-cart-btn"
            >
              <ShoppingBag className="w-4 h-4 mr-1" />
              {txt.addToCart}
            </Button>
          </div>
        </div>
      </div>

      <ProductDetailModal
        item={selectedCateringItem}
        isOpen={!!selectedCateringItem}
        onClose={() => setSelectedCateringItem(null)}
        onAddToCart={null}
        language={language}
      />
    </div>
  );
};

export default Catering;
